<template>
  <div>
    <b-row class="content-header">
      <b-col
        class="content-header-left mb-2"
        cols="12"
        md="12"
      >
        <b-row class="breadcrumbs-top">
          <b-col cols="12">
            <h1 class="custom-header-title">
              Editing {{ catalog.name }}
            </h1>
          </b-col>
        </b-row>
      </b-col>
    </b-row>

    <b-form @submit.prevent="submitForm">
      <validation-observer
        ref="catalogUpdateForm"
        #default="{invalid}"
      >
        <b-card header-tag="header">
          <b-row>
            <!-- SCLC Product Catalog ID (read-only) -->
            <b-col md="12">
              <b-form-group
                label="SCLC Product Catalog ID"
                label-cols-md="3"
                label-for="stringID"
              >
                <b-form-input
                  id="stringID"
                  :value="catalog.stringID"
                  name="stringID"
                  disabled
                />
              </b-form-group>
            </b-col>

            <!-- Catalog Item Name (editable) -->
            <b-col md="12">
              <b-form-group
                label="Catalog Item Name*"
                label-cols-md="3"
                label-for="name"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Catalog Item Name"
                  vid="name"
                  rules="required"
                >
                  <b-form-input
                    id="name"
                    v-model="name"
                    :state="(errors.length > 0) ? false : null"
                    name="name"
                    placeholder="Catalog Item Name"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                  <small
                    v-if="nameValidation"
                    class="text-danger"
                  >{{ nameError }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- Product Category (read-only) -->
            <b-col md="12">
              <b-form-group
                label="Product Category"
                label-cols-md="3"
                label-for="category"
              >
                <b-form-input
                  id="category"
                  :value="catalog.category ? catalog.category.name : ''"
                  name="category"
                  disabled
                />
              </b-form-group>
            </b-col>

            <!-- Product Code (read-only) -->
            <b-col md="12">
              <b-form-group
                label="Product Code"
                label-cols-md="3"
                label-for="productCodes"
              >
                <b-form-input
                  id="productCodes"
                  :value="productCodeNames"
                  name="productCodes"
                  disabled
                />
              </b-form-group>
            </b-col>

            <!-- Image (editable) -->
            <b-col
              md="12"
              class="my-2"
            >
              <b-form-group
                label="Image"
                label-cols-md="3"
                label-for="image"
              >
                <div class="d-flex justify-content-start">
                  <div class="image__block">
                    <b-img
                      class="mb-1 mb-sm-0 user__avatar img-preview-block"
                      :src="fileSrc ? fileSrc : defaultImage"
                    />
                  </div>
                  <div class="upload__buttons my-auto mx-2">
                    <p>{{ imageNameOriginal }}</p>
                    <b-form-file
                      ref="fileInput"
                      v-model="image"
                      type="file"
                      accept="image/jpeg, image/jpg, image/png"
                      style="display: none"
                      @input="setImage"
                    />
                    <b-button
                      variant="primary"
                      class="mr-50"
                      @click="$refs.fileInput.$el.childNodes[0].click()"
                    >
                      Upload
                    </b-button>
                    <b-button
                      v-if="fileSrc"
                      variant="outline-primary"
                      class="mr-50"
                      @click="removeFileSrc"
                    >
                      Remove
                    </b-button>
                  </div>
                </div>
              </b-form-group>
            </b-col>

            <!-- SCLC Package ID (read-only) -->
            <b-col md="12">
              <b-form-group
                label="SCLC Package ID"
                label-cols-md="3"
                label-for="sclcID"
              >
                <b-form-input
                  id="sclcID"
                  :value="catalog.sclcID"
                  name="sclcID"
                  disabled
                />
              </b-form-group>
            </b-col>

            <!-- SCLC Package Code (read-only) -->
            <b-col md="12">
              <b-form-group
                label="SCLC Package Code"
                label-cols-md="3"
                label-for="code"
              >
                <b-form-input
                  id="code"
                  :value="catalog.code"
                  name="code"
                  disabled
                />
              </b-form-group>
            </b-col>

            <!-- SCLC Description (read-only) -->
            <b-col md="12">
              <b-form-group
                label="SCLC Description"
                label-cols-md="3"
                label-for="sclcName"
              >
                <b-form-input
                  id="sclcName"
                  :value="catalog.sclcName"
                  name="sclcName"
                  disabled
                />
              </b-form-group>
            </b-col>

            <!-- Notes for Agent (editable, rich text) -->
            <b-col md="12">
              <b-form-group
                label="Notes for Agent"
                label-cols-md="3"
                label-for="agentNote"
              >
                <quill-editor
                  ref="quillTextEditor"
                  class="editor d-block"
                  :value="agentNote"
                  :options="editorOption"
                  @change="({ html }) => { agentNote = html }"
                />
              </b-form-group>
            </b-col>

            <!-- Status (editable) -->
            <b-col md="12">
              <b-form-group
                label="Status*"
                label-cols-md="3"
                label-for="status"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Status"
                  vid="status"
                  rules="required"
                >
                  <v-select
                    id="status"
                    v-model="status"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="['On Sale', 'Not On Sale']"
                    :clearable="false"
                    placeholder="Select Status"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>

        <b-nav class="wrap-border save-nav">
          <li class="nav-item">
            <span>You're currently editing a Catalog Item. Please save to avoid losing your data.</span>
          </li>
          <li class="nav-item ml-auto">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="button"
              variant="flat-primary"
              @click="cancelRedirect"
            >
              Cancel
            </b-button>
          </li>
          <li class="nav-item mr-1">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="submit"
              variant="success"
              :disabled="invalid"
            >
              <feather-icon
                icon="SaveIcon"
                class="mr-50"
              />
              <span class="align-middle">Save Changes</span>
            </b-button>
          </li>
        </b-nav>
      </validation-observer>
    </b-form>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BCard, BRow, BCol, BFormGroup, BFormInput, BForm, BButton, BNav, BImg, BFormFile,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { required } from '@validations'
import { quillEditor } from 'vue-quill-editor'
import vSelect from 'vue-select'
import store from '@/store/index'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BButton,
    BNav,
    BImg,
    BFormFile,
    vSelect,
    quillEditor,
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  setup() {
    const editorOption = {
      modules: {
        toolbar: [
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['clean'],
        ],
      },
      theme: 'snow',
      placeholder: '',
    }
    return { editorOption }
  },
  data() {
    return {
      // eslint-disable-next-line global-require
      defaultImage: require('@/assets/images/placeholder/placeholder_image.png'),
      catalog: {},
      name: '',
      agentNote: '',
      status: '',
      image: null,
      imageName: '',
      imageNameOriginal: '',
      fileSrc: '',
      nameError: 'Valid Name is required',
      nameValidation: false,
      prevRoute: null,
      required,
    }
  },
  computed: {
    productCodeNames() {
      if (!this.catalog.productCodes || !this.catalog.productCodes.length) return ''
      return this.catalog.productCodes.map(pc => pc.name).join(', ')
    },
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      // eslint-disable-next-line no-param-reassign
      vm.prevRoute = from
    })
  },
  created() {
    this.$http.get(`reservation/product-catalog/${this.$route.params.id}`)
      .then(response => {
        this.catalog = response.data
        this.name = response.data.name || ''
        this.agentNote = response.data.agentNote || ''
        this.status = response.data.status || ''
        this.fileSrc = response.data.image || null
        if (response.data.image) {
          this.imageName = response.data.image.split('/').pop()
          this.imageNameOriginal = this.imageName
        }

        store.commit('breadcrumb/REMOVE_BREADCRUMB')
        store.commit('breadcrumb/UPDATE_BREADCRUMB', {
          to: `/products/catalog/${this.$route.params.id}/show`,
          text: this.name,
        })
        store.commit('breadcrumb/UPDATE_BREADCRUMB', {
          active: true,
          text: `Editing ${this.name}`,
        })
      })
      .catch(error => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  },
  methods: {
    setImage() {
      if (this.image) {
        if (this.image.size <= 5242880) {
          this.imageNameOriginal = this.image.name
          const reader = new FileReader()
          reader.readAsDataURL(this.image)
          reader.onload = () => {
            this.fileSrc = reader.result
          }
        } else {
          this.fileSrc = null
          this.imageNameOriginal = null
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'File size not accepted more than 5MB',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        }
      }
    },
    removeFileSrc() {
      this.fileSrc = null
      this.image = null
      this.imageNameOriginal = null
    },
    cancelRedirect() {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.push({ name: 'products-catalog-index' })
      }
    },
    submitForm() {
      this.$refs.catalogUpdateForm.validate().then(async success => {
        if (success) {
          store.commit('appConfig/UPDATE_CONTINUOUS_LOADER', true)
          const formData = new FormData()

          if (this.image) {
            const res = await this.uploadFileToAws(this.image)
            const { fileUrl, fileName, upload } = res
            if (upload && fileUrl) {
              formData.append('image', fileName)
              formData.append('imageStorage', 'aws')
            } else {
              formData.append('catalogImage', this.image)
            }
          } else if (this.fileSrc) {
            formData.append('imageStorage', this.catalog.imageStorage || 'local')
            formData.append('image', this.imageName)
          }

          formData.append('name', this.name)
          formData.append('agentNote', this.agentNote || '')
          formData.append('status', this.status)

          store.commit('appConfig/UPDATE_CONTINUOUS_LOADER', false)

          this.$http.patch(`reservation/product-catalog/${this.$route.params.id}/update`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
            .then(() => {
              this.$swal({
                title: 'Catalog Updated',
                html: '',
                // eslint-disable-next-line global-require
                imageUrl: require('@/assets/images/icons/success.png'),
                imageWidth: 80,
                imageHeight: 80,
                imageAlt: 'Custom Icon',
                showCancelButton: false,
                confirmButtonText: 'Okay',
                customClass: {
                  confirmButton: 'btn btn-primary mt-1',
                },
                buttonsStyling: false,
              }).then(result => {
                if (result.value) {
                  if (window.history.length > 1) {
                    this.$router.back()
                  } else {
                    this.$router.push({ name: 'products-catalog-index' })
                  }
                }
              })
            })
            .catch(error => {
              store.commit('appConfig/UPDATE_CONTINUOUS_LOADER', false)
              if (error.data.errors) {
                error.data.errors.forEach(validationError => {
                  if (validationError.param === 'name') {
                    this.nameError = validationError.msg
                    this.nameValidation = true
                  }
                })
                const items = document.getElementsByClassName('text-danger')
                window.scrollTo({ top: items[0].offsetTop, behavior: 'smooth' })
              } else {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
                    icon: 'AlertTriangleIcon',
                    variant: 'danger',
                  },
                })
              }
            })
        }
      })
    },
  },
}
</script>

<style scoped>
.img-preview-block {
  box-sizing: border-box;
  width: 100px;
  height: 100px;
  border: 1px solid #D2DCEA;
  border-radius: 5px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/quill.scss';
</style>
